import React, { useEffect } from 'react';
import { CMSProvider, useCMS } from './context/CMSContext';
import { Navbar } from './components/public/Navbar';
import { HeroSection } from './components/public/HeroSection';
import { StorySection } from './components/public/StorySection';
import { PhilosophySection } from './components/public/PhilosophySection';
import { ProjectsSection } from './components/public/ProjectsSection';
import { Footer } from './components/public/Footer';
import { ToastContainer } from './components/common/ToastContainer';
import { BackToTop } from './components/common/BackToTop';
import { SmoothScroll } from './components/common/SmoothScroll';
import { SiteLoader } from './components/common/SiteLoader';
import { SEORuntimeManager } from './components/common/SEORuntimeManager';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AlertTriangle } from 'lucide-react';

// Code-split heavy Admin CMS to keep public initial bundle ultra-lightweight
const AdminPanel = React.lazy(() => import('./components/admin/AdminPanel').then(m => ({ default: m.AdminPanel })));
const AdminLogin = React.lazy(() => import('./components/admin/AdminLogin').then(m => ({ default: m.AdminLogin })));
const MaintenancePage = React.lazy(() => import('./components/public/MaintenancePage').then(m => ({ default: m.MaintenancePage })));

// Code-split secondary public pages
const ProjectsPage = React.lazy(() => import('./components/public/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const ProjectDetailPage = React.lazy(() => import('./components/public/ProjectDetailPage').then(m => ({ default: m.ProjectDetailPage })));
const AboutPage = React.lazy(() => import('./components/public/AboutPage').then(m => ({ default: m.AboutPage })));
const PhilosophyPage = React.lazy(() => import('./components/public/PhilosophyPage').then(m => ({ default: m.PhilosophyPage })));
const WhyOltrayaPage = React.lazy(() => import('./components/public/WhyOltrayaPage').then(m => ({ default: m.WhyOltrayaPage })));
const InvestorGroupPage = React.lazy(() => import('./components/public/InvestorGroupPage').then(m => ({ default: m.InvestorGroupPage })));
const LeadershipPage = React.lazy(() => import('./components/public/LeadershipPage').then(m => ({ default: m.LeadershipPage })));
const LeadershipDetailPage = React.lazy(() => import('./components/public/LeadershipDetailPage').then(m => ({ default: m.LeadershipDetailPage })));
const JournalPage = React.lazy(() => import('./components/public/JournalPage').then(m => ({ default: m.JournalPage })));
const JournalDetailPage = React.lazy(() => import('./components/public/JournalDetailPage').then(m => ({ default: m.JournalDetailPage })));
const Mumbai3Page = React.lazy(() => import('./components/public/Mumbai3Page').then(m => ({ default: m.Mumbai3Page })));
const ConnectPage = React.lazy(() => import('./components/public/ConnectPage').then(m => ({ default: m.ConnectPage })));
const FaqsPage = React.lazy(() => import('./components/public/FaqsPage').then(m => ({ default: m.FaqsPage })));
const PrivacyPage = React.lazy(() => import('./components/public/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import('./components/public/TermsPage').then(m => ({ default: m.TermsPage })));

// Code-split modals
const ProspectusModal = React.lazy(() => import('./components/public/ProspectusModal').then(m => ({ default: m.ProspectusModal })));
const ProjectDetailModal = React.lazy(() => import('./components/public/ProjectDetailModal').then(m => ({ default: m.ProjectDetailModal })));
const StoryModal = React.lazy(() => import('./components/public/StoryModal').then(m => ({ default: m.StoryModal })));
const ConnectModal = React.lazy(() => import('./components/public/ConnectModal').then(m => ({ default: m.ConnectModal })));
const FaqsModal = React.lazy(() => import('./components/public/FaqsModal').then(m => ({ default: m.FaqsModal })));

const MainLayout: React.FC = () => {
  const { 
    data,
    isAdminMode, 
    isAdminAuthenticated,
    setActiveAdminTab,
    activePublicPage,
    selectedLeaderId,
    selectedJournalSlug,
    isInitialLoading,
    isProspectusModalOpen,
    isProjectDetailModalOpen,
    isStoryModalOpen,
    isConnectModalOpen,
    isFaqsModalOpen
  } = useCMS();

  const maintenance = data.siteSettings?.maintenanceMode;
  const isMaintenanceActive = Boolean(maintenance?.isEnabled);

  const [isBypassed, setIsBypassed] = React.useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const secretKey = (maintenance?.secretBypassKey || 'preview').toLowerCase();
      if (
        urlParams.get('preview') === 'true' ||
        urlParams.get('preview') === secretKey ||
        urlParams.get('key') === secretKey ||
        urlParams.get('bypass') === 'true'
      ) {
        sessionStorage.setItem('oltraya_maintenance_bypass', 'true');
        return true;
      }
      return sessionStorage.getItem('oltraya_maintenance_bypass') === 'true';
    }
    return false;
  });

  // Scroll to top automatically whenever the public route changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [activePublicPage, selectedLeaderId, selectedJournalSlug]);

  // Dynamically sync and update Favicon across every page
  useEffect(() => {
    const favicon = data.siteSettings?.faviconUrl || data.siteSettings?.logoImageUrl;
    if (favicon) {
      const rels = ['icon', 'shortcut icon', 'apple-touch-icon'];
      rels.forEach(rel => {
        let link: HTMLLinkElement | null = document.querySelector(`link[rel='${rel}']`) || document.querySelector(`link[rel~='${rel}']`);
        if (!link) {
          link = document.createElement('link');
          link.rel = rel;
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = favicon;
        if (favicon.startsWith('data:image/svg')) {
          link.type = 'image/svg+xml';
        } else if (favicon.startsWith('data:image/png') || favicon.endsWith('.png')) {
          link.type = 'image/png';
        }
      });
    }
  }, [data.siteSettings?.faviconUrl, data.siteSettings?.logoImageUrl]);


  if (isAdminMode) {
    if (!isAdminAuthenticated) {
      return (
        <ErrorBoundary scope="admin">
          <React.Suspense fallback={<div className="min-h-screen bg-[#0a0c10]" />}>
            <AdminLogin />
            <ToastContainer />
          </React.Suspense>
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary scope="admin">
        <React.Suspense fallback={<div className="min-h-screen bg-[#111317]" />}>
          <div className="min-h-screen bg-[#111317] admin-theme">
            {isMaintenanceActive && (
              <div className="bg-amber-600/90 text-white px-4 py-2 text-xs font-semibold flex items-center justify-between border-b border-amber-500 shadow-md">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-200 animate-pulse" />
                  <span>
                    Maintenance Mode is <strong>ACTIVE</strong>. Live visitors currently see the Coming Soon / Maintenance screen.
                  </span>
                </div>
                <button
                  onClick={() => setActiveAdminTab('settings')}
                  className="px-2.5 py-1 bg-black/40 hover:bg-black/60 text-white text-[11px] font-medium border border-white/20 transition-all cursor-pointer"
                >
                  Configure in Settings
                </button>
              </div>
            )}
            <AdminPanel />
            <ToastContainer />
          </div>
        </React.Suspense>
      </ErrorBoundary>
    );
  }

  // Public Visitors: Check Maintenance Mode
  if (isMaintenanceActive && !isBypassed) {
    return (
      <React.Suspense fallback={<div className="min-h-screen bg-[#0d0f13]" />}>
        <SEORuntimeManager />
        <MaintenancePage onBypass={() => setIsBypassed(true)} />
        <ToastContainer />
      </React.Suspense>
    );
  }

  const renderCurrentPage = () => {
    switch (activePublicPage) {
      case 'project-detail':
        return <ProjectDetailPage />;
      case 'projects':
        return <ProjectsPage />;
      case 'mumbai-3':
        return <Mumbai3Page />;
      case 'about':
        return <AboutPage />;
      case 'philosophy':
        return <PhilosophyPage />;
      case 'why-oltraya':
        return <WhyOltrayaPage />;
      case 'investor-group':
        return <InvestorGroupPage />;
      case 'leadership':
        return <LeadershipPage />;
      case 'leadership-detail':
        return <LeadershipDetailPage />;
      case 'journal':
        return <JournalPage />;
      case 'journal-detail':
        return <JournalDetailPage />;
      case 'faqs':
        return <FaqsPage />;
      case 'connect':
        return <ConnectPage />;
      case 'privacy':
        return <PrivacyPage />;
      case 'terms':
        return <TermsPage />;
      case 'home':
      default:
        return (
          <div className="min-h-screen bg-[#121417] text-[#e8e6e3] selection:bg-[#a88345]/30 selection:text-[#f8f6f0]">
            <Navbar />
            <main>
              {/* 1. Hero Section */}
              <HeroSection />

              {/* 2. Story Section */}
              <StorySection />

              {/* Divider Line */}
              <div className="w-full bg-[#f7f2e8] px-6 md:px-12">
                <div className="max-w-7xl mx-auto border-t border-[#ded8cc]" />
              </div>

              {/* 3. Philosophy Section */}
              <PhilosophySection />

              {/* Divider Line */}
              <div className="w-full bg-[#f7f2e8] px-6 md:px-12">
                <div className="max-w-7xl mx-auto border-t border-[#ded8cc]" />
              </div>

              {/* 4. Featured Projects Section */}
              <ProjectsSection />
            </main>
            <Footer />
          </div>
        );
    }
  };

  const pageTransitionKey = `${activePublicPage}-${selectedLeaderId || ''}-${selectedJournalSlug || ''}`;

  return (
    <SmoothScroll>
      <div className="relative">
        {/* Real-Time SEO Engine, Metadata, Schema, Redirects & Code Injector */}
        <SEORuntimeManager />

        <React.Suspense fallback={<div className="min-h-screen bg-[#121417]" />}>
          <div key={pageTransitionKey} className="page-flow-transition min-h-screen">
            {renderCurrentPage()}
          </div>

          {/* Global Interactive Modals (Loaded on-demand only when triggered) */}
          {isProspectusModalOpen && <ProspectusModal />}
          {isProjectDetailModalOpen && <ProjectDetailModal />}
          {isStoryModalOpen && <StoryModal />}
          {isConnectModalOpen && <ConnectModal />}
          {isFaqsModalOpen && <FaqsModal />}
        </React.Suspense>

        {/* Notification Toast Stack */}
        <ToastContainer />

        {/* Sleek Progressive Back To Top Component */}
        {!isInitialLoading && <BackToTop />}

        {/* Initial Luxury Screen Loader Overlay */}
        <SiteLoader isVisible={isInitialLoading} />
      </div>
    </SmoothScroll>
  );
};

export default function App() {
  return (
    <ErrorBoundary scope="global">
      <CMSProvider>
        <MainLayout />
      </CMSProvider>
    </ErrorBoundary>
  );
}
